<template>
  <div class="two-factor-auth-wrapper">
    <!-- Transparent in-component loader overlay -->
    <div v-if="overlayLoading" class="mfa-overlay-loader">
      <loader :is-comp-loader="true" />
    </div>

    <img
      v-if="teamLogo && !loading && !hideLogo"
      :src="teamLogo"
      class="logo"
      alt="Team-Logo"
    />
    <div v-if="!loading" class="two-factor-auth">
      <!-- Screen 1: Send Code -->
      <div v-if="!codeSent" class="box">
        <div class="title-desc">
          <h3 class="title">Verification</h3>
          <p class="desc">
            We will send a verification code to your<br />
            {{
              sendCodeMethod === "message" ? "phone number" : "email address"
            }}
            to access your portal
          </p>
        </div>

        <div class="request-code">
          <h2>
            {{ sendCodeMethod === "message" ? maskedPhone : maskedEmail }}
          </h2>
          <button type="button" class="btn btn-primary" @click="sendCode">
            Send code
          </button>
          <button
            v-if="sendCodeMethod === 'message' && userPhone"
            type="button"
            class="btn-link update-phone-link"
            @click="openUpdatePhoneModal"
          >
            Update Phone Number
          </button>
        </div>

        <div class="text-button" v-if="userEmail && userPhone">
          <p class="text-secondary">or choose another method</p>
          <div class="button-link">
            <button
              v-if="userEmail"
              type="button"
              class="btn-link"
              @click="
                sendCodeMethod = 'email';
                sendCode();
              "
            >
              Receive an email
            </button>
            <button
              v-if="userPhone"
              type="button"
              class="btn-link"
              @click="
                sendCodeMethod = 'message';
                sendCode();
              "
            >
              Receive a text
            </button>
          </div>
        </div>

        <!-- Email-only: Add Phone Number in the same style as "Receive a text" -->
        <div class="text-button" v-if="!userPhone && userEmail">
          <div class="button-link">
            <button
              type="button"
              class="btn-link"
              @click="openUpdatePhoneModal"
            >
              Add Phone Number
            </button>
          </div>
        </div>
      </div>

      <!-- Screen 2: Enter Code -->
      <div v-else class="box">
        <div class="title-desc">
          <h3 class="title">Enter your verification code</h3>
          <p class="desc">
            Provide the verification code sent to
            {{ sendCodeMethod === "message" ? maskedPhone : maskedEmail }}
            to access your portal
          </p>
        </div>

        <div class="code-input">
          <input
            type="text"
            pattern="\d*"
            inputmode="numeric"
            class="input-box"
            :class="digits[0] ? 'code-entered' : ''"
            maxlength="1"
            ref="digit0"
            v-model="digits[0]"
            @input="onDigitInput(0)"
            @keydown="onDigitKeydown($event, 0)"
            @paste="onPaste"
          />
          <input
            type="text"
            pattern="\d*"
            inputmode="numeric"
            class="input-box"
            :class="digits[1] ? 'code-entered' : ''"
            maxlength="1"
            ref="digit1"
            v-model="digits[1]"
            @input="onDigitInput(1)"
            @keydown="onDigitKeydown($event, 1)"
          />
          <input
            type="text"
            pattern="\d*"
            inputmode="numeric"
            class="input-box"
            :class="digits[2] ? 'code-entered' : ''"
            maxlength="1"
            ref="digit2"
            v-model="digits[2]"
            @input="onDigitInput(2)"
            @keydown="onDigitKeydown($event, 2)"
          />
          <input
            type="text"
            pattern="\d*"
            inputmode="numeric"
            class="input-box"
            :class="digits[3] ? 'code-entered' : ''"
            maxlength="1"
            ref="digit3"
            v-model="digits[3]"
            @input="onDigitInput(3)"
            @keydown="onDigitKeydown($event, 3)"
          />
          <input
            type="text"
            pattern="\d*"
            inputmode="numeric"
            class="input-box"
            :class="digits[4] ? 'code-entered' : ''"
            maxlength="1"
            ref="digit4"
            v-model="digits[4]"
            @input="onDigitInput(4)"
            @keydown="onDigitKeydown($event, 4)"
          />
          <input
            type="text"
            pattern="\d*"
            inputmode="numeric"
            class="input-box"
            :class="digits[5] ? 'code-entered' : ''"
            maxlength="1"
            ref="digit5"
            v-model="digits[5]"
            @input="onDigitInput(5)"
            @keydown="onDigitKeydown($event, 5)"
          />
        </div>

        <div class="mt-4 request-code">
          <button
            ref="verifyBtn"
            type="button"
            class="btn btn-primary"
            @click="verifyCode"
            :disabled="!isCodeComplete"
          >
            Verify
          </button>

          <p class="mt-4" v-show="!showOtpTimer">
            Still haven't received a code? Has your code expired?
          </p>
          <button
            v-show="!showOtpTimer"
            type="button"
            class="mt-2 btn-link"
            @click="sendCode"
          >
            Resend Code
          </button>
          <p class="mt-3 otp-rate-limit" v-show="showOtpTimer">
            Resend in 00:{{ otpTimer }} seconds
          </p>
        </div>

        <div
          class="text-button"
          v-show="!showOtpTimer && userEmail && userPhone"
        >
          <p class="text-secondary">or choose another method</p>
          <div class="button-link">
            <button
              v-if="userEmail"
              type="button"
              class="btn-link"
              @click="
                sendCodeMethod = 'email';
                sendCode();
              "
            >
              Receive an email
            </button>
            <button
              v-if="userPhone"
              type="button"
              class="btn-link"
              @click="
                sendCodeMethod = 'message';
                sendCode();
              "
            >
              Receive a text
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Update Mobile Number Modal -->
    <div
      v-if="showUpdatePhoneModal"
      class="mfa-modal-overlay"
      @click.self="closeUpdatePhoneModal"
    >
      <div class="mfa-modal">
        <div class="mfa-modal-header">
          <h3>{{ phoneModalMode === 'add' ? 'Add Phone Number' : 'Update Phone Number' }}</h3>
          <button
            type="button"
            class="mfa-modal-close"
            @click="closeUpdatePhoneModal"
            aria-label="Close"
          >
            &times;
          </button>
        </div>
        <div class="mfa-modal-body">
          <p class="mfa-modal-desc">
            {{ phoneModalMode === 'add'
              ? 'Add a phone number to receive your verification code via text.'
              : 'Enter a new phone number to receive your verification code.' }}
          </p>
          <label class="mfa-modal-label">Phone Number</label>
          <input
            type="tel"
            inputmode="tel"
            class="mfa-modal-input"
            placeholder="e.g. 555-123-4567"
            v-model="newPhone"
            @input="onNewPhoneInput"
            maxlength="14"
          />
          <small v-if="newPhoneError" class="mfa-modal-error">{{
            newPhoneError
          }}</small>
          <small v-if="newPhoneSuccess" class="mfa-modal-success">{{
            newPhoneSuccess
          }}</small>
        </div>
        <div class="mfa-modal-footer">
          <button
            type="button"
            class="btn btn-cancel"
            @click="closeUpdatePhoneModal"
          >
            Cancel
          </button>
          <button
            type="button"
            class="btn btn-primary"
            :disabled="!isNewPhoneValid"
            @click="updatePhone"
          >
            {{ phoneModalMode === 'add' ? 'Add' : 'Update' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import CryptoJS from "crypto-js";
import commonFunctions from "./../mixins/commonfunctions";
import Loader from "./Loader.vue";

export default {
  name: "TwoFactorAuth",
  mixins: [commonFunctions],
  components: { Loader },
  props: {
    prospectId: {
      type: String,
      required: true,
    },
    hideLogo: {
      type: Boolean,
      default: false,
    },
    useCompLoader: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      loading: true,
      overlayLoading: false,
      codeSent: false,
      userEmail: "",
      userPhone: "",
      isDataEncrypted: false,
      sendCodeMethod: "message",
      digits: ["", "", "", "", "", ""],
      showOtpTimer: false,
      otpTimer: "",
      showUpdatePhoneModal: false,
      phoneModalMode: "update",
      newPhone: "",
      newPhoneError: "",
      newPhoneSuccess: "",
    };
  },
  created() {
    // Timer references stored on the instance (not in data to avoid
    // vue/no-reserved-keys lint error with underscore-prefixed keys).
    this.otpInterval = null;
    this.modalTimeout = null;
  },
  computed: {
    ...mapGetters(["getOrgId", "getAssets"]),

    teamLogo() {
      return this.getAssets?.horizontalTeamLogo || "";
    },

    maskedEmail() {
      if (!this.userEmail) return "";
      const index = this.userEmail.indexOf("@");
      if (index <= 0) return this.userEmail;
      const startIndex = Math.floor(index * 0.2);
      return (
        this.userEmail.slice(0, startIndex) +
        this.userEmail.slice(startIndex, index).replace(/./g, "*") +
        this.userEmail.slice(index)
      );
    },

    maskedPhone() {
      if (!this.userPhone) return "";
      const cleaned = this.userPhone.replace(/\D+/g, "");
      if (cleaned.length < 4) return this.userPhone;
      return "xxx-xxx-" + cleaned.slice(-4);
    },

    isCodeComplete() {
      return this.digits.every((d) => d !== "");
    },

    isNewPhoneValid() {
      const cleaned = (this.newPhone || "").replace(/\D/g, "");
      return cleaned.length === 10;
    },
  },
  async mounted() {
    // Parent's appLogin started the loader (comp or global).
    // Keep it on until GetAsset completes and all data is ready,
    // then reveal the fully populated MFA screen.
    try {
      await this.getAssetDetails();

      // Phone is default; fall back to email if no phone on file.
      if (!this.userPhone) {
        this.sendCodeMethod = "email";
      }
    } catch (error) {
      console.error("MFA init error:", error);
    }

    this.loading = false;
    this.setLoader(false);
  },
  beforeDestroy() {
    if (this.otpInterval) clearInterval(this.otpInterval);
    if (this.modalTimeout) clearTimeout(this.modalTimeout);
  },
  methods: {
    ...mapActions(["fetchLoaderStatus", "fetchCompLoaderStatus"]),

    setLoader(on) {
      if (this.useCompLoader) this.fetchCompLoaderStatus(on);
      else this.fetchLoaderStatus(on);
    },

    aesEncryption(otp) {
      const iv = CryptoJS.enc.Utf8.parse("qazwsxedcrfvtgby");
      const key = CryptoJS.enc.Utf8.parse("zaqxswcdevfrbgtn");
      return CryptoJS.AES.encrypt(otp, key, {
        iv: iv,
        mode: CryptoJS.mode.CBC,
      }).toString();
    },

    aesDecryption(encrypted) {
      const iv = CryptoJS.enc.Utf8.parse("qazwsxedcrfvtgby");
      const key = CryptoJS.enc.Utf8.parse("zaqxswcdevfrbgtn");
      return CryptoJS.AES.decrypt(encrypted, key, {
        iv: iv,
        mode: CryptoJS.mode.CBC,
      }).toString(CryptoJS.enc.Utf8);
    },

    async getAssetDetails() {
      try {
        const response = await this.$http.get(
          "/account/GetAsset/" + this.prospectId + "/" + this.getOrgId
        );
        const resp = response.data;

        if (resp && resp.assetData) {
          this.isDataEncrypted = resp.assetData.isDataEncrypted || false;
          let email = resp.assetData.email || "";
          let phone = resp.assetData.phone || "";

          if (this.isDataEncrypted) {
            if (email) email = this.aesDecryption(email);
            if (phone) phone = this.aesDecryption(phone);
          }

          this.userEmail = email;
          this.userPhone = phone;
        }
      } catch (error) {
        console.error("GetAsset error:", error);
        this.err("Failed to retrieve account details. Please try again.");
      }
    },

    clearDigits() {
      this.digits = ["", "", "", "", "", ""];
    },

    onDigitInput(index) {
      // Allow only single digit
      const val = this.digits[index];
      if (val && !/^\d$/.test(val)) {
        this.$set(this.digits, index, "");
        return;
      }

      // Auto-focus next input
      if (val && index < 5) {
        this.$refs["digit" + (index + 1)].focus();
      }

      // Auto-focus verify button when all filled
      if (val && index === 5 && this.isCodeComplete) {
        this.$refs.verifyBtn.focus();
      }
    },

    onDigitKeydown(event, index) {
      // Handle backspace — move to previous input
      if (event.key === "Backspace" && !this.digits[index] && index > 0) {
        this.$refs["digit" + (index - 1)].focus();
      }
    },

    onPaste(event) {
      event.preventDefault();
      const pasted = (event.clipboardData || window.clipboardData)
        .getData("text")
        .replace(/\D/g, "")
        .slice(0, 6);

      for (let i = 0; i < 6; i++) {
        this.$set(this.digits, i, pasted[i] || "");
      }

      // Focus last filled input or verify button
      if (pasted.length >= 6) {
        this.$nextTick(() => this.$refs.verifyBtn.focus());
      } else if (pasted.length > 0) {
        this.$nextTick(() => this.$refs["digit" + pasted.length].focus());
      }
    },

    async sendCode() {
      // Hide content and keep loader on until OTP is successfully sent.
      this.loading = true;
      this.setLoader(true);
      this.clearDigits();

      try {
        const response = await this.$http.get(
          "/account/prospectAuth" +
            "?isBorrower=true" +
            "&msgOremail=" +
            this.sendCodeMethod +
            "&prospectId=" +
            this.prospectId +
            "&orgId=" +
            this.getOrgId +
            "&skipNotification=false"
        );

        const resp = response.data;

        if (resp.status === 400) {
          this.codeSent = false;
          this.err(resp.message);
        }

        if (resp.status === 200) {
          this.codeSent = true;
          this.resendOtpTimer();

          this.$nextTick(() => {
            if (this.$refs.digit0) this.$refs.digit0.focus();
          });
        }
      } catch (error) {
        console.error("Send OTP error:", error);
        this.err("Failed to send verification code. Please try again.");
        this.codeSent = false;
      }

      this.loading = false;
      this.setLoader(false);
    },

    async verifyCode() {
      this.setLoader(true);

      const otpPlain = this.digits.join("");
      const encryptedOtp = this.aesEncryption(otpPlain);

      try {
        const response = await this.$http.patch(
          "/account/validateClientPortalOtpCode",
          {
            bisborrower: true,
            encryptedOTPCode: encryptedOtp,
            orgId: this.getOrgId,
            recordId: this.prospectId,
          }
        );

        const resp = response.data;

        if (resp.status === 200) {
          this.$emit("otp-verified");
        } else {
          this.clearDigits();
          this.err(
            resp.message || "Invalid verification code. Please try again."
          );

          this.$nextTick(() => {
            if (this.$refs.digit0) this.$refs.digit0.focus();
          });
          this.setLoader(false);
        }
      } catch (error) {
        console.error("Verify OTP error:", error);
        this.err("Verification failed. Please try again.");
        this.clearDigits();
        this.setLoader(false);
      }
    },

    resendOtpTimer() {
      // Clear any previous timer to avoid stacking
      if (this.otpInterval) clearInterval(this.otpInterval);

      this.showOtpTimer = true;
      this.otpTimer = 29;

      this.otpInterval = setInterval(() => {
        this.otpTimer--;
        this.otpTimer =
          this.otpTimer < 10 ? "0" + this.otpTimer : this.otpTimer;

        if (this.otpTimer <= 0) {
          this.showOtpTimer = false;
          clearInterval(this.otpInterval);
          this.otpInterval = null;
        }
      }, 1000);
    },

    formatPhoneDisplay(digits) {
      if (!digits) return "";
      const cleaned = digits.replace(/\D/g, "").slice(0, 10);
      if (cleaned.length > 6)
        return cleaned.slice(0, 3) + "-" + cleaned.slice(3, 6) + "-" + cleaned.slice(6);
      if (cleaned.length > 3)
        return cleaned.slice(0, 3) + "-" + cleaned.slice(3);
      return cleaned;
    },

    openUpdatePhoneModal() {
      this.phoneModalMode = this.userPhone ? "update" : "add";
      this.newPhone = this.userPhone ? this.formatPhoneDisplay(this.userPhone) : "";
      this.newPhoneError = "";
      this.newPhoneSuccess = "";
      this.showUpdatePhoneModal = true;
    },

    closeUpdatePhoneModal() {
      this.showUpdatePhoneModal = false;
      this.newPhone = "";
      this.newPhoneError = "";
      this.newPhoneSuccess = "";
    },

    onNewPhoneInput() {
      // Format phone as XXX-XXX-XXXX
      const cleaned = (this.newPhone || "").replace(/\D/g, "").slice(0, 10);
      if (cleaned.length > 6) {
        this.newPhone =
          cleaned.slice(0, 3) +
          "-" +
          cleaned.slice(3, 6) +
          "-" +
          cleaned.slice(6);
      } else if (cleaned.length > 3) {
        this.newPhone = cleaned.slice(0, 3) + "-" + cleaned.slice(3);
      } else {
        this.newPhone = cleaned;
      }
      this.newPhoneError = "";
      this.newPhoneSuccess = "";
    },

    async updatePhone() {
      if (!this.isNewPhoneValid) {
        this.newPhoneError = "Please enter a valid 10-digit phone number.";
        return;
      }

      this.newPhoneError = "";
      this.newPhoneSuccess = "";
      this.overlayLoading = true;

      const cleaned = this.newPhone.replace(/\D/g, "");
      const payload = {
        prospectId: this.prospectId,
        orgId: this.getOrgId,
        phoneNo: cleaned,
      };

      try {
        const response = await this.$http.post(
          "/account/UpdateProspectPhone",
          payload
        );
        const resp = response.data;

        // API returns { status: "Success", message: "...", data: {...} }
        const isSuccess = resp && resp.status === "Success";

        if (isSuccess) {
          this.userPhone = cleaned;
          this.sendCodeMethod = "message";
          this.newPhoneSuccess = "Phone Number updated successfully";
          this.overlayLoading = false;

          // Show green confirmation briefly, then auto-close modal.
          // Send Code screen will show updated masked number with
          // mobile selected by default.
          if (this.modalTimeout) clearTimeout(this.modalTimeout);
          this.modalTimeout = setTimeout(() => {
            this.closeUpdatePhoneModal();
            this.modalTimeout = null;
          }, 1200);
          return;
        }

        this.newPhoneError =
          (resp && resp.message) || "Failed to update phone number.";
      } catch (error) {
        console.error("Update phone error:", error);
        this.newPhoneError = "Failed to update phone number. Please try again.";
      }

      this.overlayLoading = false;
    },
  },
};
</script>

<style scoped>
@import "./../assets/css/two-factor-auth.css";
</style>
